<script setup>
import { reactive, computed, watch, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const sourceData = computed(() => $stores.appData?.state?.data?.general || {});

// Form sections separated for independent tracking
const tutorials = reactive({
  withdrawVideoLink: '',
  withdrawTutorialLink: '',
  rechargeVideoLink: '',
  rechargeTutorialLink: '',
  iphoneInstallationVideoLink: ''
});

const assistance = reactive({
  whatsappComplaintLink: '',
  whatsappAssistanceLink: '',
  telegramAssistanceLink: '',
  whatsappBotLink: '',
  telegramBotLink: '',
  email: ''
});

const socialAndLegal = reactive({
  whatsappLink: '',
  facebookLink: '',
  instagramLink: '',
  telegramLink: '',
  tiktokLink: '',
  websiteLink: '',
  policyUrl: '',
  cguUrl: '',
  faqUrl: ''
});

const otherLinks = reactive({
  snapchatLink: '',
  passwordResetUrl: '',
  affiliationDetailText: ''
});

import { TRANSACTIONS } from '../../transaction/constants';
import platform from '@core/platform';

const rechargeServices = computed(() => 
  TRANSACTIONS.SERVICES_LIST.filter(s => s.group === 'recharges')
);

const downloads = reactive({
  netflixDownloadUrl: '',
  crunchyrolDownloadUrl: '',
  xbetDownloadUrl: '',
  betwinnerDownloadUrl: '',
  oneWinDownloadUrl: '',
  primeVideoDownloadUrl: '',
  spotifyDownloadUrl: '',
  nordVPNDownloadUrl: '',
  privateVPNDownloadUrl: '',
  cyberGhostDownloadUrl: ''
});

const currency = reactive({
  userCurrency: ''
});

// Initialize all sections
watch(
  sourceData,
  (data) => {
    if (!data) return;

    Object.assign(tutorials, {
      withdrawVideoLink: data.withdrawVideoLink || '',
      withdrawTutorialLink: data.withdrawTutorialLink || '',
      rechargeVideoLink: data.rechargeVideoLink || '',
      rechargeTutorialLink: data.rechargeTutorialLink || '',
      iphoneInstallationVideoLink: data.iphoneInstallationVideoLink || '',
    });

    Object.assign(assistance, {
      whatsappComplaintLink: data.whatsappComplaintLink || '',
      whatsappAssistanceLink: data.whatsappAssistanceLink || '',
      telegramAssistanceLink: data.telegramAssistanceLink || '',
      whatsappBotLink: data.whatsappBotLink || '',
      telegramBotLink: data.telegramBotLink || '',
      email: data.email || ''
    });

    Object.assign(socialAndLegal, {
      whatsappLink: data.whatsappLink || '',
      facebookLink: data.facebookLink || '',
      instagramLink: data.instagramLink || '',
      telegramLink: data.telegramLink || '',
      tiktokLink: data.tiktokLink || '',
      websiteLink: data.websiteLink || '',
      policyUrl: data.policyUrl || '',
      cguUrl: data.cguUrl || '',
      faqUrl: data.faqUrl || ''
    });

    Object.assign(otherLinks, {
      snapchatLink: data.snapchatLink || '',
      passwordResetUrl: data.passwordResetUrl || '',
      affiliationDetailText: data.affiliationDetailText || ''
    });

    Object.assign(downloads, {
      netflixDownloadUrl: data.netflixDownloadUrl || '',
      crunchyrolDownloadUrl: data.crunchyrolDownloadUrl || '',
      xbetDownloadUrl: data.xbetDownloadUrl || '',
      betwinnerDownloadUrl: data.betwinnerDownloadUrl || '',
      oneWinDownloadUrl: data.oneWinDownloadUrl || '',
      primeVideoDownloadUrl: data.primeVideoDownloadUrl || '',
      spotifyDownloadUrl: data.spotifyDownloadUrl || '',
      nordVPNDownloadUrl: data.nordVPNDownloadUrl || '',
      privateVPNDownloadUrl: data.privateVPNDownloadUrl || '',
      cyberGhostDownloadUrl: data.cyberGhostDownloadUrl || ''
    });

    Object.assign(currency, {
      userCurrency: data.userCurrency || ''
    });
  },
  { immediate: true }
);

// Check if section has changes
const sectionChanged = (section, sourceKey = null) => {
  return Object.keys(section).some((key) => section[key] !== sourceData.value?.[key]);
};

const hasChanges = computed(
  () =>
    sectionChanged(tutorials) ||
    sectionChanged(assistance) ||
    sectionChanged(socialAndLegal) ||
    sectionChanged(otherLinks) ||
    sectionChanged(downloads) ||
    sectionChanged(currency)
);

function save() {
  if (!hasChanges.value) return;

  const payload = { general: {} };
  const allSections = {
    tutorials,
    assistance,
    socialAndLegal,
    otherLinks,
    downloads,
    currency
  };

  Object.keys(allSections).forEach((sectionKey) => {
    const section = allSections[sectionKey];
    Object.keys(section).forEach((key) => {
      if (section[key] !== sourceData.value?.[key]) {
        payload.general[key] = section[key];
      }
    });
  });

  $bus.emit($constants.EVENTS.APP_DATA.UPDATE_GENERAL, payload);
}

function resetSection(section) {
  Object.keys(section).forEach((key) => {
    section[key] =
      sourceData.value?.[key] || (typeof section[key] === 'number' ? 0 : '');
  });
}

function resetAll() {
  resetSection(tutorials);
  resetSection(assistance);
  resetSection(socialAndLegal);
  resetSection(otherLinks);
  resetSection(downloads);
  resetSection(currency);
}
</script>

<template>
  <div class=" space-y-8">
    <!-- HEADER -->
    <div class="flex justify-between items-center sticky top-0 py-2 z-2">
      <h2 class="text-lg font-semibold">Paramètres généraux</h2>
      <div class="flex gap-4" v-show="hasChanges">
        <button
          class="px-6 py-2 rounded-xl bg-gray-300 text-gray-700"
          @click="resetAll"
        >
          Annuler tout
        </button>
        <button
          class="px-6 py-2 rounded-xl bg-primary-600 text-white"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- SOCIAL AND LEGAL -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">Liens sociaux et légaux</h3>
        <button
          v-if="sectionChanged(socialAndLegal)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(socialAndLegal)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label class="block mb-1 text-sm">WhatsApp</label>
          <input class="inputComponent" v-model="socialAndLegal.whatsappLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Facebook</label>
          <input class="inputComponent" v-model="socialAndLegal.facebookLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Instagram</label>
          <input class="inputComponent" v-model="socialAndLegal.instagramLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Telegram</label>
          <input class="inputComponent" v-model="socialAndLegal.telegramLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">TikTok</label>
          <input class="inputComponent" v-model="socialAndLegal.tiktokLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Site Web</label>
          <input class="inputComponent" v-model="socialAndLegal.websiteLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm font-medium">Politique de confidentialité</label>
          <input class="inputComponent" v-model="socialAndLegal.policyUrl" />
        </div>
        <div>
          <label class="block mb-1 text-sm font-medium">Conditions Générales</label>
          <input class="inputComponent" v-model="socialAndLegal.cguUrl" />
        </div>
        <div>
          <label class="block mb-1 text-sm font-medium">FAQ</label>
          <input class="inputComponent" v-model="socialAndLegal.faqUrl" />
        </div>
      </div>
    </section>

    <!-- TUTORIALS -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">Tutoriels</h3>
        <button
          v-if="sectionChanged(tutorials)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(tutorials)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm">Vidéo retrait</label>
          <input class="inputComponent" v-model="tutorials.withdrawVideoLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Tutoriel retrait</label>
          <input
            class="inputComponent"
            v-model="tutorials.withdrawTutorialLink"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Vidéo recharge</label>
          <input class="inputComponent" v-model="tutorials.rechargeVideoLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Tutoriel recharge</label>
          <input
            class="inputComponent"
            v-model="tutorials.rechargeTutorialLink"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Vidéo instalation sur Iphone</label>
          <input
            class="inputComponent"
            v-model="tutorials.iphoneInstallationVideoLink"
          />
        </div>
      </div>
    </section>

    <!-- ASSISTANCE -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">Assistance</h3>
        <button
          v-if="sectionChanged(assistance)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(assistance)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm">WhatsApp réclamations</label>
          <input
            class="inputComponent"
            v-model="assistance.whatsappComplaintLink"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">WhatsApp assistance</label>
          <input
            class="inputComponent"
            v-model="assistance.whatsappAssistanceLink"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Telegram assistance</label>
          <input
            class="inputComponent"
            v-model="assistance.telegramAssistanceLink"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Email</label>
          <input class="inputComponent" v-model="assistance.email" />
        </div>
        <div>
          <label class="block mb-1 text-sm">WhatsApp Bot Link</label>
          <input
            class="inputComponent"
            v-model="assistance.whatsappBotLink"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Telegram Bot Link</label>
          <input
            class="inputComponent"
            v-model="assistance.telegramBotLink"
          />
        </div>
      </div>
    </section>

    <!-- OTHER LINKS -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">Autres liens</h3>
        <button
          v-if="sectionChanged(otherLinks)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(otherLinks)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm">Snapchat</label>
          <input class="inputComponent" v-model="otherLinks.snapchatLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Reset mot de passe</label>
          <input class="inputComponent" v-model="otherLinks.passwordResetUrl" />
        </div>
        <div class="md:col-span-2">
          <label class="block mb-1 text-sm">Texte affiliation</label>
          <textarea
            class="inputComponent h-20 resize-none"
            v-model="otherLinks.affiliationDetailText"
          ></textarea>
        </div>
      </div>
    </section>

    <!-- DOWNLOAD URLS -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">
          Liens de téléchargement
        </h3>
        <button
          v-if="sectionChanged(downloads)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(downloads)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm">Netflix</label>
          <input
            class="inputComponent"
            v-model="downloads.netflixDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Crunchyroll</label>
          <input
            class="inputComponent"
            v-model="downloads.crunchyrolDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">1xBet</label>
          <input class="inputComponent" v-model="downloads.xbetDownloadUrl" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Betwinner</label>
          <input
            class="inputComponent"
            v-model="downloads.betwinnerDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">1Win</label>
          <input class="inputComponent" v-model="downloads.oneWinDownloadUrl" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Prime Video</label>
          <input
            class="inputComponent"
            v-model="downloads.primeVideoDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Spotify</label>
          <input
            class="inputComponent"
            v-model="downloads.spotifyDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Nord VPN</label>
          <input
            class="inputComponent"
            v-model="downloads.nordVPNDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Private VPN</label>
          <input
            class="inputComponent"
            v-model="downloads.privateVPNDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">CyberGhost</label>
          <input
            class="inputComponent"
            v-model="downloads.cyberGhostDownloadUrl"
          />
        </div>
      </div>
    </section>

  </div>
</template>

